import { MINUTE_GAP } from "Components/Calendar/CalendarEntryHelper";
import { CellSize } from "Components/Calendar/CalendarHelper";
import { MergedCalendarEntry } from "Components/CalendarMonth/CalendarMonthHelper";
import { useIsIsoWeekdayEnabled } from "Components/CompanySetting/CompanySettingHelper";
import { useSetWorkingHours } from "Components/WorkingHours/helpers/useSetWorkingHours";
import { MinuteRange } from "Components/WorkingHours/types/MinuteRange";
import { ScheduleEntry, SchedulePhase } from "domain-entities";
import moment, { Moment } from "moment";
import { getResizeMinutes } from "./helpers/getResizeMinutes";
import { getMinutesFromMoment } from "Components/WorkingHours/helpers/getMinutesFromMoment";

export interface ResizePixel {
	pixel: number;
	dateTime: Moment;
}

export interface ResizePixelSet {
	left: ResizePixel[];
	right: ResizePixel[];
}

export interface BlockingResizePixel {
	left?: ResizePixel;
	right?: ResizePixel;
}

export function useGetMonthScheduleEntriesResizePixelSet() {
	const setWorkingHours = useSetWorkingHours();
	const isIsoWeekdayEnabled = useIsIsoWeekdayEnabled();

	return (
		startOfCalendar: Moment,
		endOfCalendar: Moment,
		cellSize: CellSize
	) => {
		const resizePixelSet: ResizePixelSet = {
			left: [],
			right: [],
		};

		const calendarDiff = endOfCalendar.diff(startOfCalendar, "days");
		let disabledIsoWeekdaysLength = 0;

		for (let index = 0; index < calendarDiff; index++) {
			const day = startOfCalendar.clone().add(index, "days");

			if (isIsoWeekdayEnabled(day.isoWeekday())) {
				const pixel =
					cellSize.month.nameWidth +
					cellSize.offset.left +
					(index - disabledIsoWeekdaysLength) * cellSize.month.dayWidth;

				resizePixelSet.left.push({
					pixel: pixel,
					dateTime: setWorkingHours(day.clone(), "start"),
				});

				resizePixelSet.right.push({
					pixel: pixel + cellSize.month.dayWidth - 1,
					dateTime: setWorkingHours(day.clone(), "end"),
				});
			} else {
				disabledIsoWeekdaysLength++;
			}
		}

		return resizePixelSet;
	};
}

export function getWeekScheduleEntriesResizePixelSet(
	visibleMinutes: MinuteRange,
	workingMinutes: MinuteRange,
	startOfWeek: Moment,
	cellSize: CellSize
) {
	const resizeMinutes = getResizeMinutes(visibleMinutes, workingMinutes);
	const resizePixelSet: ResizePixelSet = {
		left: [],
		right: [],
	};

	for (let weekday = 0; weekday <= 6; weekday++) {
		const weekdayPixel =
			cellSize.week.nameWidth +
			cellSize.offset.left +
			weekday * cellSize.week.dayWidth;

		resizeMinutes.forEach((resizeMinute) => {
			const left =
				((resizeMinute - visibleMinutes.start) * cellSize.week.dayWidth) /
				(visibleMinutes.end - visibleMinutes.start);
			const pixel = Math.round(weekdayPixel + left);

			if (
				resizeMinute + MINUTE_GAP <=
				resizeMinutes[resizeMinutes.length - 1]
			) {
				resizePixelSet.left.push({
					pixel: pixel,
					dateTime: startOfWeek
						.clone()
						.add(weekday, "days")
						.add(resizeMinute, "minutes"),
				});
			}

			if (resizeMinute - MINUTE_GAP >= resizeMinutes[0]) {
				resizePixelSet.right.push({
					pixel: pixel - 1,
					dateTime: startOfWeek
						.clone()
						.add(weekday, "days")
						.add(resizeMinute, "minutes"),
				});
			}
		});
	}

	return resizePixelSet;
}

export function getBlockingScheduleEntries(
	scheduleEntry: ScheduleEntry,
	scheduleEntries: ScheduleEntry[]
) {
	const blockingScheduleEntries = scheduleEntries
		.filter((item) => item.id !== scheduleEntry.id)
		.filter((item) =>
			scheduleEntry.projectId
				? item.projectId
				: item.typeId === scheduleEntry.typeId
		);

	const leftItemEndTime = [...blockingScheduleEntries]
		.sort((a, b) => b.endTime - a.endTime)
		.find((item) => item.endTime <= scheduleEntry.endTime)?.endTime;

	const rightItemStartTime = [...blockingScheduleEntries]
		.sort((a, b) => a.startTime - b.startTime)
		.find((item) => item.startTime >= scheduleEntry.startTime)?.startTime;

	return {
		leftItemEndTime,
		rightItemStartTime,
	};
}

export function useGetMonthBlockingResizePixel() {
	const setWorkingHours = useSetWorkingHours();

	return (
		mergedScheduleEntry: MergedCalendarEntry,
		mergedScheduleEntries: MergedCalendarEntry[]
	) => {
		const blockingResizePixel: BlockingResizePixel = {};
		const { leftItemEndTime, rightItemStartTime } = getBlockingScheduleEntries(
			mergedScheduleEntry.calendarEntry,
			mergedScheduleEntries.map((item) => item.calendarEntries).flat()
		);

		if (leftItemEndTime) {
			blockingResizePixel.left = {
				dateTime: setWorkingHours(
					moment.unix(leftItemEndTime).startOf("day").add(1, "day"),
					"start"
				),
				pixel: 0, // Not used
			};
		}

		if (rightItemStartTime) {
			blockingResizePixel.right = {
				dateTime: setWorkingHours(
					moment.unix(rightItemStartTime).startOf("day").subtract(1, "day"),
					"end"
				),
				pixel: 0, // Not used
			};
		}

		return blockingResizePixel;
	};
}

export function getWeekBlockingResizePixel(
	scheduleEntry: ScheduleEntry,
	scheduleEntries: ScheduleEntry[],
	visibleMinutes: MinuteRange,
	cellSize: CellSize
) {
	const blockingResizePixel: BlockingResizePixel = {};
	let { leftItemEndTime, rightItemStartTime } = getBlockingScheduleEntries(
		scheduleEntry,
		scheduleEntries
	);

	if (leftItemEndTime) {
		const endMoment = moment.unix(leftItemEndTime);
		const endMinutes = getMinutesFromMoment(endMoment);
		if (endMinutes === visibleMinutes.end) {
			leftItemEndTime = endMoment
				.startOf("day")
				.add(1, "day")
				.add(visibleMinutes.start, "minutes")
				.unix();
		}

		blockingResizePixel.left = {
			dateTime: moment.unix(leftItemEndTime),
			pixel: getWeekPixelFromDateTime(
				leftItemEndTime,
				visibleMinutes,
				cellSize
			),
		};
	}

	if (rightItemStartTime) {
		const startMoment = moment.unix(rightItemStartTime);
		const startMinutes = getMinutesFromMoment(startMoment);
		if (startMinutes === visibleMinutes.start) {
			rightItemStartTime = startMoment
				.startOf("day")
				.subtract(1, "day")
				.add(visibleMinutes.end, "minutes")
				.unix();
		}

		blockingResizePixel.right = {
			dateTime: moment.unix(rightItemStartTime),
			pixel:
				getWeekPixelFromDateTime(rightItemStartTime, visibleMinutes, cellSize) -
				1,
		};
	}

	return blockingResizePixel;
}

export function getWeekPixelFromDateTime(
	dateTime: number,
	visibleMinutes: MinuteRange,
	cellSize: CellSize
) {
	const timeMoment = moment.unix(dateTime);
	const timeMinutes = getMinutesFromMoment(timeMoment);

	const offsetPixel = cellSize.week.nameWidth + cellSize.offset.left;
	const left =
		((timeMinutes - visibleMinutes.start) * cellSize.week.dayWidth) /
		(visibleMinutes.end - visibleMinutes.start);

	return Math.round(
		offsetPixel + timeMoment.weekday() * cellSize.week.dayWidth + left
	);
}

export function getScheduleEntryResizePixelSet(
	resizePixelSet: ResizePixelSet,
	blockingResizePixel: BlockingResizePixel
): ResizePixelSet {
	const result: ResizePixelSet = { ...resizePixelSet };

	if (blockingResizePixel.left) {
		const blockingResizePixelLeft = blockingResizePixel.left;
		result.left = [blockingResizePixel.left, ...resizePixelSet.left].filter(
			(item) => item.dateTime >= blockingResizePixelLeft.dateTime
		);
	}

	if (blockingResizePixel.right) {
		const blockingResizePixelRight = blockingResizePixel.right;
		result.right = [...resizePixelSet.right, blockingResizePixel.right].filter(
			(item) => item.dateTime <= blockingResizePixelRight.dateTime
		);
	}

	return result;
}

export function getSchedulePhaseResizePixelSet(
	resizePixelSet: ResizePixelSet,
	schedulePhases: SchedulePhase[],
	schedulePhase?: SchedulePhase
): ResizePixelSet {
	if (schedulePhase) {
		const blockingSchedulePhases = schedulePhases.filter(
			(phase) => phase.id !== schedulePhase.id
		);

		let blockingEndDate = -Infinity;
		let blockingStartDate = Infinity;

		blockingSchedulePhases.forEach((phase) => {
			if (
				phase.endDate < schedulePhase.startDate &&
				phase.endDate > blockingEndDate
			) {
				blockingEndDate = phase.endDate;
			}

			if (
				phase.startDate > schedulePhase.endDate &&
				phase.endDate < blockingStartDate
			) {
				blockingStartDate = phase.startDate;
			}
		});

		return {
			left: resizePixelSet.left.filter(
				(resizePixel) => resizePixel.dateTime.unix() > blockingEndDate
			),
			right: resizePixelSet.right.filter(
				(resizePixel) => resizePixel.dateTime.unix() < blockingStartDate
			),
		};
	} else {
		return resizePixelSet;
	}
}

/**
 * Credits:
 * https://stackoverflow.com/questions/8584902/get-closest-number-out-of-array/19277804#19277804
 */
export function getClosestResizePixel(
	resizePixels: ResizePixel[],
	value: number
) {
	return resizePixels.reduce((prev, curr) => {
		return Math.abs(curr.pixel - value) < Math.abs(prev.pixel - value)
			? curr
			: prev;
	});
}

export function getDayScheduleEntriesResizePixelSet(
	visibleMinutes: MinuteRange,
	startOfDay: Moment,
	cellSize: CellSize
) {
	const resizePixelSet: ResizePixelSet = {
		left: [],
		right: [],
	};
	const offsetPixel =
		cellSize.day.nameWidth +
		cellSize.offset.left;
	const totalVisibleMinutes = visibleMinutes.end - visibleMinutes.start;

	// Generate 5-minute increments within the visible range
	for (let minute = visibleMinutes.start; minute <= visibleMinutes.end; minute += 5) {
		const minuteOffsetFromStart = minute - visibleMinutes.start;
		const left =
			(minuteOffsetFromStart * totalVisibleMinutes * cellSize.day.hourWidth/60) /
			totalVisibleMinutes;
		const pixel = Math.round(offsetPixel + left);
		const currentDateTime = startOfDay.clone().add(minute, "minutes");

		// Add pixel for resizing the left side (needs to be >= start)
		if (minute + MINUTE_GAP <= visibleMinutes.end) {
			resizePixelSet.left.push({
				pixel: pixel,
				dateTime: currentDateTime,
			});
		}

		// Add pixel for resizing the right side (needs to be > start)
		if (minute - MINUTE_GAP >= visibleMinutes.start) {
			resizePixelSet.right.push({
				pixel: pixel - 1, // Adjust by -1 to align with week view logic
				dateTime: currentDateTime,
			});
		}
	}

	return resizePixelSet;
}

export function getDayPixelFromDateTime(
	dateTime: number,
	visibleMinutes: MinuteRange,
	cellSize: CellSize
) {
	const timeMoment = moment.unix(dateTime);
	const timeMinutes = getMinutesFromMoment(timeMoment);

	const offsetPixel = cellSize.day.nameWidth + cellSize.offset.left;
	const left =
		((timeMinutes - visibleMinutes.start) * cellSize.day.hourWidth) /
		(visibleMinutes.end - visibleMinutes.start);

	return Math.round(offsetPixel + left);
}

export function getDayBlockingResizePixel(
	scheduleEntry: ScheduleEntry,
	scheduleEntries: ScheduleEntry[],
	visibleMinutes: MinuteRange,
	cellSize: CellSize
) {
	const blockingResizePixel: BlockingResizePixel = {};
	let { leftItemEndTime, rightItemStartTime } = getBlockingScheduleEntries(
		scheduleEntry,
		scheduleEntries
	);

	if (leftItemEndTime) {
		const endMoment = moment.unix(leftItemEndTime);
		const endMinutes = getMinutesFromMoment(endMoment);
		if (endMinutes === visibleMinutes.end) {
			leftItemEndTime = endMoment
				.startOf("day")
				.add(visibleMinutes.start, "minutes")
				.unix();
		}

		blockingResizePixel.left = {
			dateTime: moment.unix(leftItemEndTime),
			pixel: getDayPixelFromDateTime(leftItemEndTime, visibleMinutes, cellSize),
		};
	}

	if (rightItemStartTime) {
		const startMoment = moment.unix(rightItemStartTime);
		const startMinutes = getMinutesFromMoment(startMoment);
		if (startMinutes === visibleMinutes.start) {
			rightItemStartTime = startMoment
				.startOf("day")
				.add(visibleMinutes.end, "minutes")
				.unix();
		}

		blockingResizePixel.right = {
			dateTime: moment.unix(rightItemStartTime),
			pixel:
				getDayPixelFromDateTime(rightItemStartTime, visibleMinutes, cellSize) - 1,
		};
	}

	return blockingResizePixel;
}
